/**
 * A variant's effective unit price.
 *
 * A variant row priced 0 means "never configured", not "free": the admin set a
 * price on the product and left the generated variants alone. do-print carries
 * 15 such products, every variant of each priced 0 while the parent carries the
 * real number.
 *
 * `variant.price ?? product.price` does not catch this — `??` falls back only
 * on null and undefined, so a 0 wins and the shopper is shown, and charged,
 * nothing. Treating non-positive as unset is what every caller actually meant.
 *
 * The cost of this rule is that a genuinely free variant cannot be expressed as
 * 0 any more. No tenant is using it that way, and silently charging nothing is
 * the worse failure.
 */
export function effectiveVariantPrice(
  variantPrice: number | null | undefined,
  productPrice: number | null | undefined,
): number {
  if (
    typeof variantPrice === "number" &&
    Number.isFinite(variantPrice) &&
    variantPrice > 0
  ) {
    return variantPrice;
  }
  return typeof productPrice === "number" && Number.isFinite(productPrice)
    ? productPrice
    : 0;
}

type SaleAmounts = { sale_price: number; original_price: number };

/**
 * A sale saved on a whole product is a percentage of the price it was created
 * against, so it has to move with the price. Applied here to today's `price`:
 * the stored sale_price alone goes stale when the product is repriced.
 */
export function scaleSaleToPrice(
  sale: SaleAmounts,
  price: number,
): SaleAmounts | null {
  if (sale.original_price <= 0) return null;
  const ratio = sale.sale_price / sale.original_price;
  return {
    original_price: price,
    sale_price: Math.round(price * ratio * 100) / 100,
  };
}

/** Each variant keeps its own sale, else takes the product-wide discount. */
export function withVariantSales<
  V extends { price: number; sale?: SaleAmounts | null },
>(variants: V[], productSale: SaleAmounts | null, productPrice: number): V[] {
  if (!productSale) return variants;
  return variants.map((v) =>
    v.sale
      ? v
      : {
          ...v,
          sale: scaleSaleToPrice(
            productSale,
            effectiveVariantPrice(v.price, productPrice),
          ),
        },
  );
}

/**
 * The sale the PDP quotes. A simple product's is as entered; on a variable
 * product a variant's own sale wins, else the product-wide discount comes off
 * `price`, which is the selected variant's or, before a pick, the product's.
 */
export function activeSaleFor<S extends SaleAmounts>(args: {
  isVariable: boolean;
  variantSale?: SaleAmounts | null;
  productSale: S | null;
  price: number;
}): SaleAmounts | S | null {
  const { isVariable, variantSale, productSale, price } = args;
  if (!isVariable) return productSale;
  if (variantSale) return variantSale;
  return productSale ? scaleSaleToPrice(productSale, price) : null;
}
